import React, { useState } from 'react';
import { CloseCircleIcon, HamburgerIcon } from '@components/ui/Icons';
import { Dropdown, DropdownData } from '@components/ui/Dropdown';
import { RecommendationDto, convertSeverityFromNumber } from '@cloudclevr/components';
import './quill-styles.css';
import { AcceptRecommendationIcon } from '@cloudclevr/components';
import { useGetAttachmentDownloadToken } from '@hooks/roadmaps/useRecommendationMutations';
import { DownloadIcon } from '@components/ui/Icons';

// Updated interface to match backend response structure
export interface RecommendationTileProps {
  recommendation: RecommendationDto;
  onViewNotes?: (id: string) => void;
  onDownloadAttachments?: (id: string) => void;
  onViewQuote?: (id: string) => void;
  className?: string;
  onDropdownAction?: (action: string, recommendationId: string) => void;
  dropdownActions?: DropdownData[];
  onEditRecommendation?: (recommendationId: string) => void;
  currentTab?: string;
  onDownloadSuccess?: (fileName: string) => void;
  onDownloadError?: (fileName: string) => void;
  roadmapId?: string;
}

// Helper function to format statusDate
const formatStatusDate = (statusDate: string | undefined): string => {
  if (!statusDate) return '';
  
  try {
    const date = new Date(statusDate);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  } catch {
    return '';
  }
};

// Helper function to get status display info
const getStatusDisplayInfo = (currentTab: string | undefined, statusDate: string | undefined, reason: string | undefined) => {
  const formattedDate = formatStatusDate(statusDate);
  
  switch (currentTab) {
    case 'accepted':
      return {
        icon: React.createElement(AcceptRecommendationIcon, { size: "8" }),
        text: `Accepted: ${formattedDate}`,
        iconColor: 'text-green-600'
      };
    case 'inProgress':
      return {
        icon: React.createElement(AcceptRecommendationIcon, { size: "8" }),
        text: `Work started: ${formattedDate}`,
        iconColor: 'text-blue-600'
      };
    case 'completed':
      return {
        icon: React.createElement(AcceptRecommendationIcon, { size: "8"}),
        text: `Completed: ${formattedDate}`,
        iconColor: 'text-green-600'
      };
    case 'declined':
      return {
        icon: React.createElement(CloseCircleIcon, { 
          size: "4", 
          classNames: "text-red-600",
          color: "text-red-600" 
        }),
        text: `Declined: ${formattedDate}${reason ? ` : ${reason}` : ''}`,
        iconColor: 'text-red-600'
      };
    default:
      return null;
  }
};

// Risk level colors - updated to match backend severity values
const getRiskLevelColor = (severity: number) => {
  const riskLevel = convertSeverityFromNumber(severity);
  const colorMap = {
    Critical: 'text-red-600 border-red-600 bg-red-50',
    Moderate: 'text-yellow-600 bg-yellow-50 border-yellow-300',
    Low: 'text-[#37afd4] bg-blue-50 border-[#37afd4]',
    Information: 'text-gray-600 bg-gray-50 border-gray-600'
  };
  return colorMap[riskLevel];
};

const getRiskLevelPillColorIcon = (severity: number) => {
  const riskLevel = convertSeverityFromNumber(severity);
  const colorMap = {
    Critical: '#e92e2e',
    Moderate: '#dac154',
    Low: '#37afd4',
    Information: '#6b7280'
  };
  return colorMap[riskLevel];
};

const RecommendationTile: React.FC<RecommendationTileProps> = ({
  recommendation,
  onViewNotes,
  onDownloadAttachments,
  onDropdownAction,
  dropdownActions = [],
  currentTab,
  className = '',
  onDownloadSuccess,
  onDownloadError,
  roadmapId
}) => {

  const downloadTokenMutation = useGetAttachmentDownloadToken();
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  
  const handleDropdownAction = (action: string) => {
    onDropdownAction?.(action, recommendation.id);
  };

  const handleSingleFileDownload = async (attachment: any) => {
    // Prevent multiple downloads and show loading state
    if (downloadingFile === attachment.id) return;
    
    setDownloadingFile(attachment.id);

    try {
      // Get download token from backend
      const downloadUrl = await downloadTokenMutation.mutateAsync({
        roadmapId: roadmapId!,
        recommendationId: recommendation.id,
        attachmentId: attachment.id
      });

      console.log('Download URL received:', downloadUrl);

      // Use fetch to download the blob and then create download link
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      //  Call success callback for toast
      onDownloadSuccess?.(attachment.fileName);

    } catch (error) {
      console.error('Download failed:', error);
      // Call error callback for toast
      onDownloadError?.(attachment.fileName);
    } finally {
      // Clear loading state
      setDownloadingFile(null);
    }
  };

  const riskLevel = convertSeverityFromNumber(typeof recommendation.severity === 'string' ? parseInt(recommendation.severity, 10) : recommendation.severity);
  const hasNotes = recommendation.notes && recommendation.notes.trim().length > 0;
  const hasAttachments = recommendation.attachments && recommendation.attachments.length > 0;
  const attachmentCount = recommendation.attachments?.length || 0;
  
  
  const hasQuote = recommendation.quoteUrl && recommendation.quoteUrl.trim().length > 0;

  const subCategoryName = recommendation.subCategory?.name;
  const statusInfo = getStatusDisplayInfo(
    currentTab ?? undefined,
    recommendation.statusDate ?? undefined,
    recommendation.reason ?? undefined
  );

  return (    
    <div className={`bg-white border border-gray-200 rounded-lg pt-4 pb-4 shadow-sm ${className}`}>      
      {/* Header with title, risk pill, and hamburger menu */}
    <div className="flex items-center justify-between mb-4 pl-4 pr-4">
        <div className="flex-1">
          {/* Subcategory and Title */}
          <div className="text-sm text-gray-900 font-semibold">
            {subCategoryName}: <span className="pl-1">{recommendation.title || recommendation.name}</span>
          </div>
          
          {/* Status info for specific tabs */}
          {statusInfo && (
            <div className="flex items-center mt-2 text-base text-gray-700">
              <div className={`mr-1.5 ${statusInfo.iconColor}`}>
                {statusInfo.icon}
              </div>
              <span>{statusInfo.text}</span>
            </div>
          )}
        </div>
        
        {/* Right side - Risk pill and hamburger menu */}
        <div className="flex items-center space-x-3 ml-4">
          {/* Risk Level Pill */}
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRiskLevelColor(typeof recommendation.severity === 'string' ? parseInt(recommendation.severity, 10) : recommendation.severity)}`}>
            <div className={`mr-3 flex-shrink-0 pt-0.5`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke={getRiskLevelPillColorIcon(typeof recommendation.severity === 'string' ? parseInt(recommendation.severity, 10) : recommendation.severity)} strokeWidth="2" fill="none"/>
                <text x="12" y="17" textAnchor="middle" fill={getRiskLevelPillColorIcon(typeof recommendation.severity === 'string' ? parseInt(recommendation.severity, 10) : recommendation.severity)} fontSize="14" fontWeight="bold">i</text>
              </svg>
            </div>
            {riskLevel}
          </span>
          
          {/* Hamburger Menu with Dropdown */}
          {dropdownActions.length > 0 && (
            <Dropdown className="relative">
              <Dropdown.IconHeader 
                icon={<HamburgerIcon />}
                classNames="p-1 bg-white hover:bg-gray-100 rounded-full transition-colors"
                handleOpen={() => {}} 
              />
              <Dropdown.List align="right" className="z-50">
                {dropdownActions.map((action, index) => (
                  <Dropdown.ContextItem
                    key={index}
                    label={action.label}
                    icon={action.icon}
                    color={action.color}
                    disabled={action.disabled}
                    hidden={action.isHidden?.(recommendation, currentTab )} 
                    onChange={() => handleDropdownAction(action.value?.[0] || '')}
                  />
                ))}
              </Dropdown.List>
            </Dropdown>
          )}
        </div>
      </div>

      {/* Risk Section */}
      <div className="mb-4 pt-4 border-t border-gray-200">
        <div className="flex items-start space-x-3 pl-4 pr-4">
          {/* Risk Icon */}
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3.04.97 4.43L1 23l6.57-1.97C9.96 21.64 11.46 22 13 22h-1c5.52 0 10-4.48 10-10S17.52 2 12 2z" 
                      fill="white" stroke="white" strokeWidth="1"/>
                <circle cx="12" cy="12" r="8" fill="black"/>
                <path d="M12 7v6M12 17h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          
          {/* Risk Content */}
          <div className="flex-1">
            <div className="mb-1 pt-1">
              <span className="text-sm font-semibold text-gray-900">Risk</span>
            </div>
            <div className="text-[1rem] text-black rich-html-content"
                  dangerouslySetInnerHTML={{
                    __html: recommendation.description || 'No risk description available'
                  }}
                />
              <div>
                
              </div>
          </div>
        </div>
      </div>

      {/* Recommendation Section */}
      <div className="mb-4 pt-4 border-t border-gray-200">
        <div className="flex items-start space-x-3 pl-4 pr-4">
          {/* Recommendation Icon */}
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round">
                <path d="M10 2a5 5 0 0 1 5 5c0 1.5-.8 2.8-2 3.6v1.4a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-1.4C5.8 9.8 5 8.5 5 7a5 5 0 0 1 5-5z"/>
                <path d="M8 15h4"/>
                <path d="M9 17h2"/>
              </svg>
            </div>
          </div>
          
          {/* Recommendation Content */}
          <div className="flex-1">
            <div className="pt-1">
              <span className="text-sm font-semibold text-gray-900">Recommendation</span>
            </div>
            <div className="text-[1rem] text-black rich-html-content" 
              dangerouslySetInnerHTML={{
                __html: recommendation.reccomendation || 'No recommendation available'
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer Actions with separators */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center pl-4 pr-4">
          {/* View notes */}
          {hasNotes && (
            <button
              onClick={() => onViewNotes?.(recommendation.id)}
              className="flex items-center text-sm text-gray-700 hover:text-gray-900 transition-colors underline underline-offset-4 decoration-[1px] decoration-gray-300 cursor-pointer"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View notes
            </button>
          )}

          {/* Separator after View notes */}
          {hasNotes && (hasAttachments || hasQuote) && (
            <div className="w-px h-4 bg-gray-300 mx-4"></div>
          )}

          {/* Download attachments */}
          {hasAttachments && (
            <>
              {attachmentCount === 1 ? (
                // Single file - show as clickable filename
                <button
                    onClick={() => handleSingleFileDownload(recommendation.attachments[0])}
                    disabled={downloadingFile === recommendation.attachments[0].id}
                    className={`flex items-center text-sm text-gray-700 hover:text-gray-900 transition-colors underline underline-offset-4 decoration-[1px] decoration-gray-300 cursor-pointer ${
                      downloadingFile === recommendation.attachments[0].id ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {downloadingFile === recommendation.attachments[0].id ? (
                      // ✅ ADD: Loading spinner for single file
                      <svg className="animate-spin w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <DownloadIcon classNames='pr-2' size='5' />
                    )}
                    {recommendation.attachments[0].fileName}
                  </button>
              ) : (
                // Multiple files - show download attachments with count
                <button
                  onClick={() => onDownloadAttachments?.(recommendation.id)}                  
                  className="flex items-center text-sm text-gray-700 hover:text-gray-900 transition-colors underline underline-offset-4 decoration-[1px] decoration-gray-300 cursor-pointer"
                >
                  <DownloadIcon classNames='pr-2' size='5' />
                  Download attachments ({attachmentCount})
                </button>
              )}
            </>
          )}

          {/* Separator after View notes */}
          {(hasAttachments && hasQuote) && (
            <div className="w-px h-4 bg-gray-300 mx-4"></div>
          )}

          {/* View quote */}
          {hasQuote && (
            <a
              href={recommendation.quoteUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={recommendation.quoteUrl}
              className="flex items-center text-sm text-gray-700 hover:text-gray-900 transition-colors underline underline-offset-4 decoration-[1px] decoration-gray-300 cursor-pointer"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              View quote
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationTile;